import React from "react";
import { Box } from "@mui/material";
import { colors, fontSize, fontWeight } from "theme/defaultStyle";
import { StewartTableColumn } from "controls/global/stewart-table/StewartTable";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import InputSearch from "./InputSearch";
import SelectSearch, { SelectSearchOption } from "../../policy-management/shared/SelectSearch";

export const AR_GRID_NAME = "AccountingReceivable";

export function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null) return "$0.00";
  const prefix = value < 0 ? "-$" : "$";
  return (
    prefix +
    Math.abs(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export function getAmountColor(value: number): string {
  if (value < 0) return colors.green;
  if (value > 0) return colors.red01;
  return colors.grey11;
}

const RCPT_TYPE_LABELS: Record<string, string> = {
  JE: "JE - Journal Entry",
  CHK: "CHK - Check",
  REC: "REC - Receipt",
};

export function rcptTypeLabel(value: string): string {
  return RCPT_TYPE_LABELS[value] || value;
}

// row.date is stored as yyyy-MM-dd so it sorts correctly as plain text;
// reformat to MM/dd/yyyy for display.
export function formatDisplayDate(value: string): string {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year}`;
}

interface BuildArColumnsOptions {
  rcptTypeOptions: OptionRenderType[];
  cancelFlagOptions: SelectSearchOption<boolean>[];
  onFiltersChange: (name: keyof PremiumReceivablesReceiptRecord, value: string | number | boolean | null) => void;
  // Bumped by the caller on chip click/Reset so the Accounting Record Type
  // column's own filter dropdown remounts and clears its selection.
  rcptTypeFilterResetKey?: number;
}

export function buildArColumns(
  opts: BuildArColumnsOptions
): StewartTableColumn<PremiumReceivablesReceiptRecord>[] {
  const {
    rcptTypeOptions,
    cancelFlagOptions,
    onFiltersChange,
    rcptTypeFilterResetKey,
  } = opts;

  return [
    {
      field: "AgentID",
      name: "Agent ID",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 1,
      actionComponent: () => (
        <InputSearch name="AgentID" integer onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <Box
          component="span"
          sx={{ color: colors.blue15, fontWeight: fontWeight.bold1, fontSize: fontSize.base }}
        >
          {row.AgentID}
        </Box>
      ),
    },
    {
      field: "AgentName",
      name: "Agent Name",
      width: 200,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 2,
      actionComponent: () => (
        <InputSearch name="AgentName" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.AgentName || ""} />
      ),
    },
    {
      field: "AccountingPeriod",
      name: "Accounting Period",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 3,
      actionComponent: () => (
        <InputSearch name="AccountingPeriod" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.AccountingPeriod || ""} />
      ),
    },
    {
      field: "ReceiptDate",
      name: "Trans Date",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 4,
      actionComponent: () => (
        <InputSearch name="ReceiptDate" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={formatDisplayDate(row?.ReceiptDate ?? "")} />
      ),
    },
    {
      field: "ReceiptTypeCode",
      name: "Accounting Record Type",
      width: 190,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 5,
      // ReceiptTypeCode binds to a scalar @ReceiptTypeCode param on the proc,
      // not a list — single-select, same component PR's transactions tab uses
      // for its equivalent PremiumReceivableTypeCode column.
      actionComponent: () => (
        <SelectSearch<string>
          key={rcptTypeFilterResetKey}
          name="ReceiptTypeCode"
          options={rcptTypeOptions}
          onChange={(name, value) =>
            onFiltersChange(name as keyof PremiumReceivablesReceiptRecord, value)
          }
        />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={rcptTypeLabel(row.ReceiptTypeCode)} />
      ),
    },
    {
      field: "ReceiptNumber",
      name: "Receipt Number",
      width: 170,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 6,
      actionComponent: () => (
        <InputSearch name="ReceiptNumber" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <Box
          component="span"
          sx={{ fontFamily: "monospace", fontSize: fontSize.base }}
        >
          {row.ReceiptNumber}
        </Box>
      ),
    },
    {
      field: "ReceiptAmount",
      name: "Amount",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 7,
      actionComponent: () => (
        <InputSearch name="ReceiptAmount" numeric onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <Box
          component="span"
          sx={{
            color: getAmountColor(row.ReceiptAmount),
            fontWeight: fontWeight.bold2,
            fontSize: fontSize.base,
          }}
        >
          {formatCurrency(row.ReceiptAmount)}
        </Box>
      ),
    },
    {
      field: "GLPRINumber",
      name: "Primary Number",
      width: 140,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 8,
      actionComponent: () => (
        <InputSearch name="GLPRINumber" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.GLPRINumber || ""} />
      ),
    },
    {
      field: "GLSubNumber",
      name: "Sub Number",
      width: 120,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 9,
      actionComponent: () => (
        <InputSearch name="GLSubNumber" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.GLSubNumber || ""} />
      ),
    },
    {
      field: "GLDeptNumber",
      name: "Dept Number",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 10,
      actionComponent: () => (
        <InputSearch name="GLDeptNumber" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.GLDeptNumber || ""} />
      ),
    },
    {
      field: "BankAccountNumber",
      name: "Bank Account No",
      width: 150,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 11,
      actionComponent: () => (
        <InputSearch name="BankAccountNumber" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.BankAccountNumber || ""} />
      ),
    },
    {
      field: "IsCancel",
      name: "Cancel Flag",
      width: 130,
      sortable: true,
      resizable: true,
      draggable: true,
      position: 12,
      actionComponent: () => (
        <SelectSearch
          name="IsCancel"
          options={cancelFlagOptions}
          onChange={(name, value) =>
            onFiltersChange(name as keyof PremiumReceivablesReceiptRecord, value)
          }
        />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) =>
        row.IsCancel ? (
          <Box
            component="span"
            sx={{
              fontSize: fontSize.xsmall,
              fontWeight: fontWeight.bold2,
              borderRadius: "3px",
              padding: "2px 6px",
              backgroundColor: colors.red01,
              color: colors.white,
            }}
          >
            CANCELLED
          </Box>
        ) : (
          <Box component="span" sx={{ color: colors.grey09 }}>
            –
          </Box>
        ),
    },
    {
      field: "Comments",
      name: "Comments",
      width: 240,
      // Not supported by the proc's @SortColumn — see RECORD_TO_SORT_COLUMN.
      sortable: false,
      resizable: true,
      draggable: true,
      position: 13,
      actionComponent: () => (
        <InputSearch name="Comments" onChange={onFiltersChange} />
      ),
      valueGetter: (row: PremiumReceivablesReceiptRecord) => (
        <TooltipCell title={row.Comments || "—"} />
      ),
    },
  ];
}
